import React, { useRef, useState } from "react";
import { Table, Modal } from "reactstrap";
import { useTranslation } from "react-i18next";
import { ToolTip } from "../../AbstractElements";

const formatTests = (investigation) => {
  if (!investigation || !investigation.length) return "No tests";

  return investigation.map((inv) => (
    <div key={inv.suggestion_reason || Math.random()}>
      {inv.suggestions && inv.suggestions.length > 0 ? (
        inv.suggestions.map((suggestion) => (
          <div key={suggestion.category_name} style={{ marginBottom: "4px" }}>
            <strong>{suggestion.category_name}:</strong>&nbsp;
            {suggestion.examinations && suggestion.examinations.length > 0
              ? suggestion.examinations
                  .map((exam, examIndex) => `${examIndex + 1}) ${exam.name}`)
                  .join(", ")
              : "No tests"}
          </div>
        ))
      ) : (
        <div>No suggestions</div>
      )}
    </div>
  ));
};

const SuggestionTable = ({ suggestions = [], loading = false }) => {
  const { t } = useTranslation();
  const [tooltipOpen, setTooltipOpen] = useState({});
  const [modal, setModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const eyeButtonRefs = useRef({});

  const toggleTooltip = (id) => {
    setTooltipOpen((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  const formatDateWithMonth = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const handleView = (suggestion) => {
    setSelectedItem(suggestion);
    setModal(true);
  };

  return (
    <>
      <h6 className="mb-3 tw-font-semibold">
        {t("Investigation Suggestions")}
      </h6>
      {loading ? (
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : suggestions.length === 0 ? (
        <p className="text-center text-muted">{t("No suggestions found")}</p>
      ) : (
        <Table size="sm" striped bordered responsive>
          <thead className="thead-light">
            <tr>
              <th>{t("Tests")}</th>
              <th style={{ whiteSpace: "nowrap" }}>{t("NAKBHO Health ID")}</th>
              <th style={{ whiteSpace: "nowrap" }}>{t("Date")}</th>
              <th style={{ whiteSpace: "nowrap", textAlign: "center" }}>
                {t("Action")}
              </th>
            </tr>
          </thead>
          <tbody>
            {suggestions.map((suggestion, idx) => (
              <tr key={suggestion._id || idx}>
                <td>
                  <span
                    className="d-inline-block text-truncate"
                    style={{ whiteSpace: "nowrap" }}
                    title={formatTests(suggestion.investigation)}
                  >
                    {formatTests(suggestion.investigation)}
                  </span>
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {suggestion.patient?.health_id?.match(/.{1,4}/g)?.join(' ') || "N/A"}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {formatDate(suggestion.createdAt)}
                </td>
                <td style={{ textAlign: "center" }}>
                  <button
                    className="p-0 btn btn-link text-success small"
                    id={`ViewToolTip-${suggestion._id || idx}`}
                    ref={(el) =>
                      (eyeButtonRefs.current[suggestion._id || idx] = el)
                    }
                  >
                    <i
                      className="fa fa-eye"
                      onClick={() => {
                        if (eyeButtonRefs.current[suggestion._id || idx]) {
                          eyeButtonRefs.current[suggestion._id || idx].blur();
                        }
                        setTooltipOpen((prev) => ({
                          ...prev,
                          [suggestion._id || idx]: false,
                        }));
                        handleView(suggestion);
                      }}
                      style={{ fontSize: "14px" }}
                    ></i>
                    <ToolTip
                      attrToolTip={{
                        placement: "top",
                        isOpen: tooltipOpen[suggestion._id || idx] || false,
                        target: `ViewToolTip-${suggestion._id || idx}`,
                        toggle: () => toggleTooltip(suggestion._id || idx),
                      }}
                    >
                      <strong>{t("View")}</strong>
                    </ToolTip>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Model */}
      <Modal isOpen={modal} toggle={() => setModal(false)} size="lg">
        <div className="modal-header">
          <div className="d-flex justify-content-between w-100 align-items-center">
            <div className="d-flex justify-content-between w-100 align-items-end">
              <div className="d-flex flex-column">
                <h5 className="mb-1 modal-title">
                  Patient Name:&nbsp;
                  <strong>{selectedItem?.patient?.name || "N/A"}</strong>
                  {/* <strong>{selectedItem?.patient?.full_name || "N/A"}</strong> */}
                </h5>
                <small className="text-muted">
                  NAKBHO Health ID:&nbsp;
                  <strong>{selectedItem?.patient?.health_id?.match(/.{1,4}/g)?.join(' ') || "N/A"}</strong>
                </small>
                <small className="text-muted">
                  Gender:&nbsp;
                  <strong>
                    {selectedItem?.patient?.gender.charAt(0).toUpperCase() +
                      selectedItem?.patient?.gender.slice(1) || "N/A"}
                  </strong>
                </small>
              </div>
              <div>
                <small className="text-muted">
                  {formatDateWithMonth(selectedItem?.createdAt)}
                </small>
              </div>
            </div>

            <button
              type="button"
              className="btn-close"
              onClick={() => setModal(false)}
            ></button>
          </div>
        </div>
        <div className="modal-body">
          <div className="mb-3">
            <h6 className="mb-0">{t("Lab Test")}</h6>
          </div>

          {selectedItem?.investigation?.map((inv, invIndex) => (
            <div key={invIndex} className="mb-3">
              {inv.suggestion_reason && (
                <div className="mb-3">
                  <label className="form-label">
                    {t("Indication (Reason for test)")}
                  </label>
                  <div className="p-2 border rounded">
                    {inv.suggestion_reason}
                  </div>
                </div>
              )}

              {inv.suggestions?.map((suggestion, suggIndex) => (
                <div key={suggIndex} className="mb-3">
                  <div
                    className="p-2 mb-2 text-white rounded"
                    style={{ backgroundColor: "var(--theme-default)" }}
                  >
                    <strong>{suggestion.category_name}</strong>
                  </div>
                  <div className="flex-wrap gap-3 ps-3 d-flex">
                    {suggestion.examinations?.map((exam, examIndex) => (
                      <div
                        key={examIndex}
                        className="form-check d-flex align-items-center"
                        style={{ minWidth: "150px" }}
                      >
                        <input
                          className="form-check-input tw-w-5 tw-h-5"
                          type="checkbox"
                          checked={true}
                          id={`exam-${invIndex}-${suggIndex}-${examIndex}`}
                          style={{
                            backgroundColor: "var(--theme-default)",
                            borderColor: "var(--theme-default)",
                          }}
                          readOnly
                        />
                        <label
                          className="mb-0 form-check-label ms-2"
                          htmlFor={`exam-${invIndex}-${suggIndex}-${examIndex}`}
                        >
                          {exam.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
};

export default SuggestionTable;
